import React from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import type { StatusDetail, SlaDetail } from "../../types/performance";

type Props = {
  label: string;
  detail: StatusDetail | SlaDetail | null;
  loading: boolean;
  error: string;
  onClose: () => void;
  onPageChange: (offset: number) => void;
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const subtextStyle: React.CSSProperties = {
  margin: "6px 0 16px 0",
  fontSize: "14px",
  color: "var(--text-secondary)",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "760px",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  whiteSpace: "nowrap",
};

const paginationRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  marginTop: "14px",
  flexWrap: "wrap",
};

const paginationLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const stateStyle: React.CSSProperties = {
  padding: "20px 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

// Drill-down "sin pantalla nueva" (sección 7): sección inferior enfocada que
// se abre al hacer click en una card/fila. Muestra filas reales sin
// financials, paginadas de a `limit` (backend), consultadas exactamente
// sobre el mismo período/shippingType que el resto del dashboard.
const PerformanceDetailDrawer = ({ label, detail, loading, error, onClose, onPageChange }: Props) => {
  const total = detail?.total ?? 0;
  const limit = detail?.limit ?? 50;
  const offset = detail?.offset ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);

  return (
    <Card>
      <div style={headerRowStyle}>
        <div>
          <h2 style={titleStyle}>Detalle: {label}</h2>
          <p style={subtextStyle}>
            {total > 0
              ? `Mostrando ${from}–${to} de ${new Intl.NumberFormat("es-AR").format(total)} envío(s). Sin datos financieros.`
              : "Sin envíos en esta categoría para el período/filtro actual."}
          </p>
        </div>
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {loading ? (
        <div style={stateStyle}>Cargando detalle...</div>
      ) : error ? (
        <div style={{ ...stateStyle, color: "var(--danger)" }}>{error}</div>
      ) : !detail || detail.rows.length === 0 ? (
        <div style={stateStyle}>No hay registros para mostrar.</div>
      ) : (
        <>
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tracking</th>
                  <th style={thStyle}>Cliente</th>
                  <th style={thStyle}>Cadete</th>
                  <th style={thStyle}>Estado</th>
                  <th style={thStyle}>Fecha estado</th>
                  <th style={thStyle}>Zona</th>
                  <th style={thStyle}>Origen</th>
                </tr>
              </thead>
              <tbody>
                {detail.rows.map((row, index) => (
                  <tr key={`${row.numero_tracking ?? "sin-tracking"}-${index}`}>
                    <td style={tdStyle}>{row.numero_tracking ?? "—"}</td>
                    <td style={tdStyle}>{row.cliente ?? "—"}</td>
                    <td style={tdStyle}>{row.cadete ?? "—"}</td>
                    <td style={tdStyle}>{row.estado ?? "—"}</td>
                    <td style={tdStyle}>{row.fecha_estado ?? "—"}</td>
                    <td style={tdStyle}>{row.zona ?? "—"}</td>
                    <td style={tdStyle}>{row.origen ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={paginationRowStyle}>
            <span style={paginationLabelStyle}>
              {from}–{to} de {new Intl.NumberFormat("es-AR").format(total)}
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant="secondary" onClick={() => onPageChange(Math.max(offset - limit, 0))} disabled={offset === 0}>
                Anterior
              </Button>
              <Button variant="secondary" onClick={() => onPageChange(offset + limit)} disabled={offset + limit >= total}>
                Siguiente
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default PerformanceDetailDrawer;
